/**
 * Componente de Chat del Asistente Director IA (Jefe Virtual)
 * Solo visible para usuarios con rol 'direccion'
 * Características: Multi-modelo, Aprendizaje, Alertas Proactivas, Informes
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageCircle, X, Send, Loader2, Bot, User, 
  CheckCircle, XCircle, AlertTriangle, Trash2, Maximize2, Minimize2,
  Settings, BookOpen, Brain, ChevronDown, Bell, FileText, TrendingUp,
  Paperclip, Image, FileUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Componente para mostrar un conocimiento desplegable
const ConocimientoItem = ({ conocimiento, token, onDelete }) => {
  const [expandido, setExpandido] = useState(false);
  
  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar "${conocimiento.titulo}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/asistente/conocimientos/${conocimiento.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        onDelete();
      }
    } catch (e) {
      console.error('Error eliminando:', e);
    }
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="text-xs bg-white/20 rounded overflow-hidden">
      {/* Header - siempre visible */}
      <div 
        className="p-2 flex justify-between items-center cursor-pointer hover:bg-white/10"
        onClick={() => setExpandido(!expandido)}
      >
        <div className="flex items-center gap-2 flex-1">
          <ChevronDown className={`w-3 h-3 transition-transform ${expandido ? 'rotate-180' : ''}`} />
          <div>
            <strong>{conocimiento.titulo}</strong>
            <span className="opacity-70 ml-1">({conocimiento.categoria})</span>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); handleDelete(); }}
          className="text-red-300 hover:text-red-100 p-1"
          title="Eliminar conocimiento"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      
      {/* Contenido expandido */}
      {expandido && (
        <div className="px-3 pb-3 pt-1 border-t border-white/10">
          <div className="bg-black/20 rounded p-2 text-blue-100 whitespace-pre-wrap max-h-40 overflow-y-auto">
            {conocimiento.contenido || 'Sin contenido detallado'}
          </div>
          {conocimiento.tags && conocimiento.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {conocimiento.tags.map((tag, i) => (
                <span key={i} className="bg-blue-500/30 px-1.5 py-0.5 rounded text-[10px]">
                  #{tag}
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 text-[10px] text-blue-200 opacity-70">
            Guardado: {formatFecha(conocimiento.fecha_creacion)}
            {conocimiento.creado_por_nombre && ` por ${conocimiento.creado_por_nombre}`}
          </div>
        </div>
      )}
    </div>
  );
};

// Modelos disponibles
const MODELOS = {
  "gemini-3-pro": { nombre: "Gemini 3 Pro", emoji: "", desc: "Motor ERP" },
  "gemini-2.5-flash": { nombre: "Gemini 2.5 Flash", emoji: "", desc: "Rápido" },
  "gemini-2.5-pro": { nombre: "Gemini 2.5 Pro", emoji: "", desc: "Anterior" },
  "gpt-4o": { nombre: "GPT-4o", emoji: "", desc: "OpenAI" },
  "gpt-4o-mini": { nombre: "GPT-4o Mini", emoji: "", desc: "OpenAI rápido" },
  "claude-sonnet": { nombre: "Claude Sonnet", emoji: "", desc: "Anthropic" }
};

const AsistenteChat = ({ minimizado = true, onToggle, enPagina = false }) => {
  const { user, token } = useAuth();
  const [abierto, setAbierto] = useState(enPagina || !minimizado);
  const [mensajes, setMensajes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [accionPendiente, setAccionPendiente] = useState(null);
  const [procesandoAccion, setProcesandoAccion] = useState(false);
  const [modeloSeleccionado, setModeloSeleccionado] = useState('gemini-3-pro');
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [conocimientos, setConocimientos] = useState([]);
  const [mostrarConocimientos, setMostrarConocimientos] = useState(false);
  const [alertasProactivas, setAlertasProactivas] = useState([]);
  const [mostrarAlertas, setMostrarAlertas] = useState(false);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [previsualizacion, setPrevisualizacion] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null); // Ref para el input - evita re-renders al escribir
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [mensajes]);
  
  // Cargar historial y conocimientos al abrir por primera vez
  useEffect(() => {
    const cargarDatosIniciales = async () => {
      if (!token) return;
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Cargar historial
      try {
        const res = await fetch(`${API_URL}/api/asistente/historial?limit=20`, { headers });
        const data = await res.json();
        if (data.success && data.historial) {
          const msgs = data.historial.map(h => ({
            tipo: h.tipo,
            contenido: h.mensaje,
            timestamp: h.timestamp,
            accion: h.accion
          }));
          setMensajes(msgs);
          if (data.historial.length > 0) {
            setSessionId(data.historial[0].session_id);
          }
        }
      } catch (e) {
        console.error('Error cargando historial:', e);
      }
      
      // Cargar conocimientos
      try {
        const res = await fetch(`${API_URL}/api/asistente/conocimientos`, { headers });
        const data = await res.json();
        if (data.success) {
          setConocimientos(data.conocimientos || []);
        }
      } catch (e) {
        console.error('Error cargando conocimientos:', e);
      }
      
      // Cargar alertas proactivas
      try {
        const res = await fetch(`${API_URL}/api/asistente/alertas-proactivas`, { headers });
        const data = await res.json();
        if (data.success) {
          setAlertasProactivas(data.alertas || []);
        }
      } catch (e) {
        console.error('Error cargando alertas:', e);
      }
    };
    
    if (abierto && token) {
      cargarDatosIniciales();
    }
  }, [abierto, token]);
  
  // Solo mostrar para usuarios con rol direccion
  if (user?.rol !== 'direccion') {
    return null;
  }
  
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
  
  const cargarAlertasProactivas = async () => {
    try {
      const res = await fetch(`${API_URL}/api/asistente/alertas-proactivas`, { headers });
      const data = await res.json();
      if (data.success) {
        setAlertasProactivas(data.alertas || []);
      }
    } catch (e) {
      console.error('Error cargando alertas:', e);
    }
  };

  // Manejar selección de archivo
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tipo de archivo
    const tiposPermitidos = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!tiposPermitidos.includes(file.type)) {
      alert('Tipo de archivo no soportado. Usa: JPG, PNG, GIF, WEBP o PDF');
      return;
    }
    
    // Validar tamaño (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 10MB');
      return;
    }
    
    setArchivoSeleccionado(file);
    
    // Crear previsualización para imágenes
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPrevisualizacion(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPrevisualizacion(null);
    }
  };
  
  const quitarArchivo = () => {
    setArchivoSeleccionado(null);
    setPrevisualizacion(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Manejar pegado de imágenes desde el portapapeles
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Verificar si es una imagen
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        
        const file = item.getAsFile();
        if (!file) continue;
        
        // Validar tamaño (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert('La imagen es demasiado grande. Máximo 10MB');
          return;
        }
        
        // Crear un nombre para el archivo pegado
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
        const extension = item.type.split('/')[1] || 'png';
        const fileName = `imagen_pegada_${timestamp}.${extension}`;
        
        // Crear nuevo File con nombre
        const namedFile = new File([file], fileName, { type: item.type });
        
        setArchivoSeleccionado(namedFile);
        
        // Crear previsualización
        const reader = new FileReader();
        reader.onload = (e) => setPrevisualizacion(e.target.result);
        reader.readAsDataURL(namedFile);
        
        return;
      }
    }
  };

  const enviarMensaje = async () => {
    const inputMensaje = inputRef.current?.value || '';
    if ((!inputMensaje.trim() && !archivoSeleccionado) || enviando) return;
    
    const mensajeUsuario = inputMensaje.trim() || (archivoSeleccionado ? `Analiza este archivo: ${archivoSeleccionado.name}` : '');
    if (inputRef.current) inputRef.current.value = '';
    
    setMensajes(prev => [...prev, { 
      tipo: 'usuario', 
      contenido: mensajeUsuario, 
      timestamp: new Date().toISOString(),
      archivo: archivoSeleccionado ? {
        nombre: archivoSeleccionado.name,
        tipo: archivoSeleccionado.type,
        preview: previsualizacion
      } : null
    }]);
    setEnviando(true);
    
    try {
      if (archivoSeleccionado) {
        // Archivos: sin streaming (necesita FormData)
        const formData = new FormData();
        formData.append('mensaje', mensajeUsuario);
        formData.append('archivo', archivoSeleccionado);
        formData.append('modelo', modeloSeleccionado);
        if (sessionId) formData.append('session_id', sessionId);
        
        const res = await fetch(`${API_URL}/api/asistente/chat-con-archivo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const data = await res.json();
        quitarArchivo();
        
        if (data.success) {
          setSessionId(data.session_id);
          setMensajes(prev => [...prev, { 
            tipo: 'asistente', 
            contenido: data.respuesta,
            timestamp: data.timestamp,
            accion: data.accion_pendiente,
            modelo: data.modelo_usado || data.modelo,
            archivoAnalizado: data.archivo_procesado
          }]);
          if (data.accion_pendiente) setAccionPendiente(data.accion_pendiente);
        }
      } else {
        // Streaming para mensajes de texto
        // Add placeholder message that will be updated as chunks arrive
        const placeholderIdx = Date.now();
        setMensajes(prev => [...prev, { 
          tipo: 'asistente', 
          contenido: '',
          timestamp: new Date().toISOString(),
          modelo: modeloSeleccionado,
          _streaming: true,
          _id: placeholderIdx
        }]);
        
        const res = await fetch(`${API_URL}/api/asistente/chat-stream`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            mensaje: mensajeUsuario,
            session_id: sessionId,
            modelo: modeloSeleccionado
          })
        });
        
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.detail || `Error ${res.status}`);
        }
        
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let acumulado = '';
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              
              if (payload.error) {
                setMensajes(prev => prev.map(m => 
                  m._id === placeholderIdx 
                    ? { ...m, contenido: `Error: ${payload.error}`, tipo: 'error', _streaming: false }
                    : m
                ));
                break;
              }
              
              if (payload.session_id) {
                setSessionId(payload.session_id);
              }
              
              if (payload.chunk) {
                acumulado += payload.chunk;
                setMensajes(prev => prev.map(m => 
                  m._id === placeholderIdx 
                    ? { ...m, contenido: acumulado }
                    : m
                ));
              }
              
              if (payload.done) {
                const finalContent = payload.full_response || acumulado;
                setMensajes(prev => prev.map(m => 
                  m._id === placeholderIdx 
                    ? { ...m, contenido: finalContent, _streaming: false, accion: payload.accion_pendiente }
                    : m
                ));
                if (payload.accion_pendiente) {
                  setAccionPendiente(payload.accion_pendiente);
                }
              }
            } catch (parseErr) {
              // Skip malformed SSE lines
            }
          }
        }
      }
    } catch (e) {
      setMensajes(prev => {
        // Update streaming placeholder or add error
        const hasStreaming = prev.some(m => m._streaming);
        if (hasStreaming) {
          return prev.map(m => m._streaming 
            ? { ...m, contenido: m.contenido || ('Error: ' + e.message), tipo: m.contenido ? 'asistente' : 'error', _streaming: false }
            : m
          );
        }
        return [...prev, { tipo: 'error', contenido: 'Error: ' + e.message, timestamp: new Date().toISOString() }];
      });
    } finally {
      setEnviando(false);
    }
  };
  
  const confirmarAccion = async (confirmado) => {
    if (!accionPendiente) return;
    
    setProcesandoAccion(true);
    try {
      const res = await fetch(`${API_URL}/api/asistente/confirmar-accion`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          accion_id: accionPendiente.accion_id,
          confirmado
        })
      });
      
      const data = await res.json();
      
      setMensajes(prev => [...prev, {
        tipo: 'sistema',
        contenido: confirmado 
          ? `✅ Acción ejecutada: ${data.mensaje}`
          : '❌ Acción cancelada por el usuario',
        timestamp: new Date().toISOString()
      }]);
      
      setAccionPendiente(null);
    } catch (e) {
      console.error('Error confirmando acción:', e);
    } finally {
      setProcesandoAccion(false);
    }
  };
  
  const limpiarChat = async () => {
    if (!window.confirm('¿Limpiar todo el historial de conversación?')) return;
    
    try {
      await fetch(`${API_URL}/api/asistente/historial`, {
        method: 'DELETE',
        headers
      });
      setMensajes([]);
      setSessionId(null);
    } catch (e) {
      console.error('Error limpiando historial:', e);
    }
  };
  
  const formatearMensaje = (contenido) => {
    // Formatear markdown básico
    let html = contenido
      .replace(/```json[\s\S]*?```/g, '') // Ocultar JSON de acciones
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/### (.*?)(\n|$)/g, '<h4 class="font-bold mt-2 mb-1">$1</h4>')
      .replace(/## (.*?)(\n|$)/g, '<h3 class="font-bold text-lg mt-3 mb-1">$1</h3>')
      .replace(/- (.*?)(\n|$)/g, '<li class="ml-4">$1</li>')
      .replace(/\n/g, '<br/>');
    
    return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
  };
  
  // Si está en página dedicada, renderizar versión expandida
  if (enPagina) {
    return (
      <div className="flex flex-col h-full bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-bold text-lg">Asistente Director IA</h2>
                <p className="text-blue-100 text-sm">Tu director virtual de Duetto Catering</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setMostrarAlertas(!mostrarAlertas); setMostrarConocimientos(false); setMostrarConfig(false); }}
                className={`p-2 hover:bg-white/20 rounded-lg transition-colors relative ${mostrarAlertas ? 'bg-white/20' : ''}`}
                title="Ver alertas proactivas"
              >
                <Bell className="w-5 h-5" />
                {alertasProactivas.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {alertasProactivas.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => { setMostrarConocimientos(!mostrarConocimientos); setMostrarAlertas(false); setMostrarConfig(false); }}
                className={`p-2 hover:bg-white/20 rounded-lg transition-colors ${mostrarConocimientos ? 'bg-white/20' : ''}`}
                title="Ver conocimientos aprendidos"
              >
                <Brain className="w-5 h-5" />
              </button>
              <button
                onClick={() => { setMostrarConfig(!mostrarConfig); setMostrarAlertas(false); setMostrarConocimientos(false); }}
                className={`p-2 hover:bg-white/20 rounded-lg transition-colors ${mostrarConfig ? 'bg-white/20' : ''}`}
                title="Configurar modelo de IA"
              >
                <Settings className="w-5 h-5" />
              </button>
              <Button variant="ghost" size="sm" onClick={limpiarChat} className="text-white hover:bg-white/20">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Panel de Alertas Proactivas */}
          {mostrarAlertas && (
            <div className="mt-3 p-3 bg-red-500/20 border border-red-400/30 rounded-lg max-h-48 overflow-y-auto">
              <p className="text-xs text-red-100 mb-2 flex items-center gap-1 font-medium">
                <AlertTriangle className="w-3 h-3" />
                🚨 Alertas que requieren atención ({alertasProactivas.length}):
              </p>
              {alertasProactivas.length === 0 ? (
                <p className="text-xs text-green-200 italic">
                  ✅ No hay alertas críticas. Todo bajo control.
                </p>
              ) : (
                <div className="space-y-2">
                  {alertasProactivas.map((a, i) => (
                    <div key={i} className={`text-xs p-2 rounded ${
                      a.tipo === 'critico' ? 'bg-red-600/40' : 
                      a.tipo === 'alto' ? 'bg-orange-500/40' : 'bg-yellow-500/40'
                    }`}>
                      <strong>{a.titulo}</strong>
                      <p className="opacity-80 mt-0.5">{a.mensaje}</p>
                    </div>
                  ))}
                </div>
              )}
              <button 
                onClick={cargarAlertasProactivas}
                className="mt-2 text-xs text-blue-200 hover:text-white underline"
              >
                Actualizar alertas
              </button>
            </div>
          )}
          
          {/* Selector de modelo */}
          {mostrarConfig && (
            <div className="mt-3 p-3 bg-white/10 rounded-lg">
              <p className="text-xs text-blue-100 mb-2">Selecciona el modelo de IA:</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(MODELOS).map(([id, modelo]) => (
                  <button
                    key={id}
                    onClick={() => {
                      setModeloSeleccionado(id);
                      setMostrarConfig(false);
                    }}
                    className={`text-left p-2 rounded-lg text-sm transition-colors ${
                      modeloSeleccionado === id 
                        ? 'bg-white text-blue-600 font-medium' 
                        : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    <span className="mr-1">{modelo.emoji}</span>
                    {modelo.nombre}
                    <span className="text-xs opacity-70 ml-1">({modelo.desc})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Panel de conocimientos */}
          {mostrarConocimientos && (
            <div className="mt-3 p-3 bg-white/10 rounded-lg max-h-64 overflow-y-auto">
              <p className="text-xs text-blue-100 mb-2 flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                Conocimientos aprendidos ({conocimientos.length}):
              </p>
              {conocimientos.length === 0 ? (
                <p className="text-xs text-blue-200 italic">
                  Aún no he aprendido nada. Enséñame métodos de producción, procesos de trabajo, etc.
                </p>
              ) : (
                <div className="space-y-2">
                  {conocimientos.map((c, i) => (
                    <ConocimientoItem 
                      key={c.id || i} 
                      conocimiento={c} 
                      token={token}
                      onDelete={() => setConocimientos(prev => prev.filter(x => x.id !== c.id))}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Mensajes */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {mensajes.length === 0 && (
            <div className="text-center py-12">
              <Bot className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">¡Hola! Soy tu Asistente Director</h3>
              <p className="text-gray-500 mt-2 max-w-md mx-auto">
                Puedo ayudarte a gestionar el catering, analizar datos, detectar problemas y ejecutar acciones.
                <br/>
                <span className="text-xs text-blue-600">
                  💡 También puedes enseñarme cosas nuevas que recordaré siempre.
                </span>
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Modelo actual: {MODELOS[modeloSeleccionado]?.emoji} {MODELOS[modeloSeleccionado]?.nombre}
              </p>
              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                {[
                  '📊 Dame el análisis de rentabilidad',
                  '📋 ¿Qué tareas están pendientes?',
                  '💰 Balance financiero del mes',
                  '⚠️ ¿Hay alertas críticas?',
                  '📅 Eventos de esta semana',
                  '📄 Genera un informe semanal'
                ].map((sugerencia, i) => (
                  <button
                    key={i}
                    onClick={() => { if (inputRef.current) inputRef.current.value = sugerencia; }}
                    className="text-sm bg-white border border-gray-200 px-3 py-1.5 rounded-full hover:bg-blue-50 hover:border-blue-300 transition-colors"
                  >
                    {sugerencia}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {mensajes.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.tipo === 'usuario' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-3 ${
                msg.tipo === 'usuario' 
                  ? 'bg-blue-600 text-white' 
                  : msg.tipo === 'error'
                    ? 'bg-red-100 text-red-800 border border-red-200'
                    : msg.tipo === 'sistema'
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-white border border-gray-200 shadow-sm'
              }`}>
                <div className="flex items-start gap-2">
                  {msg.tipo === 'asistente' && <Bot className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" />}
                  {msg.tipo === 'usuario' && <User className="w-5 h-5 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1">
                    {msg.tipo === 'usuario' ? msg.contenido : formatearMensaje(msg.contenido)}
                    {msg._streaming && <span className="inline-block w-1.5 h-4 bg-blue-500 ml-0.5 animate-pulse align-text-bottom" />}
                  </div>
                </div>
                
                {/* Botones de confirmación de acción */}
                {msg.accion && msg.accion.estado === 'pendiente' && accionPendiente?.accion_id === msg.accion.accion_id && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-800 mb-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-medium">Acción pendiente de confirmación</span>
                    </div>
                    <p className="text-sm text-yellow-700 mb-3">{msg.accion.descripcion}</p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => confirmarAccion(true)}
                        disabled={procesandoAccion}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {procesandoAccion ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                        Confirmar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => confirmarAccion(false)}
                        disabled={procesandoAccion}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {enviando && !mensajes.some(m => m._streaming && m.contenido) && (
            <div className="flex justify-start">
              <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>El asistente está pensando...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input */}
        <div className="border-t bg-white p-4">
          {/* Previsualización de archivo */}
          {archivoSeleccionado && (
            <div className="mb-3 p-3 bg-blue-50 rounded-lg flex items-center gap-3">
              {previsualizacion ? (
                <img src={previsualizacion} alt="Preview" className="h-14 w-14 object-cover rounded" />
              ) : (
                <div className="h-14 w-14 bg-red-100 rounded flex items-center justify-center">
                  <FileText className="w-7 h-7 text-red-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-700 truncate">{archivoSeleccionado.name}</p>
                <p className="text-sm text-gray-500">{(archivoSeleccionado.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                onClick={quitarArchivo}
                className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
          
          <div className="flex gap-2">
            {/* Input oculto para archivos */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {/* Botón de adjuntar */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={enviando}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg border border-gray-300 disabled:opacity-50"
              title="Adjuntar imagen o PDF"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            
            <input
              ref={inputRef}
              type="text"
              defaultValue=""
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && enviarMensaje()}
              onPaste={handlePaste}
              placeholder={archivoSeleccionado ? "Describe qué quieres analizar..." : "Escribe tu mensaje o pega una imagen (Ctrl+V)..."}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={enviando}
            />
            <Button onClick={enviarMensaje} disabled={enviando}>
              {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Versión flotante
  return (
    <>
      {/* Botón flotante */}
      {!abierto && (
        <button
          onClick={() => setAbierto(true)}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all z-50 group"
        >
          <Bot className="w-6 h-6" />
          <span className="absolute right-full mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Asistente Director IA
          </span>
        </button>
      )}
      
      {/* Chat flotante */}
      {abierto && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden z-50 border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5" />
              <span className="font-medium">Asistente Director</span>
            </div>
            <div className="flex items-center gap-1">
              <button 
                onClick={limpiarChat}
                className="p-1.5 hover:bg-white/20 rounded"
                title="Limpiar chat"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => window.location.href = '/asistente'}
                className="p-1.5 hover:bg-white/20 rounded"
                title="Abrir en página completa"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setAbierto(false)}
                className="p-1.5 hover:bg-white/20 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50">
            {mensajes.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">¿En qué puedo ayudarte hoy?</p>
              </div>
            )}
            
            {mensajes.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.tipo === 'usuario' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg p-2.5 text-sm ${
                  msg.tipo === 'usuario' 
                    ? 'bg-blue-600 text-white' 
                    : msg.tipo === 'error'
                      ? 'bg-red-100 text-red-800'
                      : msg.tipo === 'sistema'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-white border border-gray-200 shadow-sm'
                }`}>
                  {msg.tipo === 'usuario' ? msg.contenido : formatearMensaje(msg.contenido)}
                  
                  {msg.accion && accionPendiente?.accion_id === msg.accion.accion_id && (
                    <div className="mt-2 pt-2 border-t border-yellow-200 flex gap-2">
                      <button 
                        onClick={() => confirmarAccion(true)}
                        className="text-xs bg-green-600 text-white px-2 py-1 rounded flex items-center gap-1"
                        disabled={procesandoAccion}
                      >
                        <CheckCircle className="w-3 h-3" /> Sí
                      </button>
                      <button 
                        onClick={() => confirmarAccion(false)}
                        className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1"
                        disabled={procesandoAccion}
                      >
                        <XCircle className="w-3 h-3" /> No
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {enviando && (
              <div className="flex justify-start">
                <div className="bg-white border rounded-lg p-2.5 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input */}
          <div className="border-t bg-white p-2">
            {/* Previsualización de archivo */}
            {archivoSeleccionado && (
              <div className="mb-2 p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                {previsualizacion ? (
                  <img src={previsualizacion} alt="Preview" className="h-12 w-12 object-cover rounded" />
                ) : (
                  <div className="h-12 w-12 bg-red-100 rounded flex items-center justify-center">
                    <FileText className="w-6 h-6 text-red-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{archivoSeleccionado.name}</p>
                  <p className="text-xs text-gray-500">{(archivoSeleccionado.size / 1024).toFixed(1)} KB</p>
                </div>
                <button
                  onClick={quitarArchivo}
                  className="p-1 text-gray-500 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            
            <div className="flex gap-2">
              {/* Input oculto para archivos */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Botón de adjuntar */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={enviando}
                className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                title="Adjuntar imagen o PDF"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              
              <input
                ref={inputRef}
                type="text"
                defaultValue=""
                onKeyDown={(e) => e.key === 'Enter' && enviarMensaje()}
                onPaste={handlePaste}
                placeholder={archivoSeleccionado ? "Describe qué quieres..." : "Mensaje o pega imagen (Ctrl+V)"}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                disabled={enviando}
              />
              <button
                onClick={enviarMensaje}
                disabled={enviando}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AsistenteChat;
